import { useEffect, useState } from 'react';
import { formatIsraeli } from '@/components/telephony/PhoneCell';
import { adminApi, type CdrDetail } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { RecordingDetail } from './RecordingDetail';
import { PlayButton, SentimentBadge, fmt, remoteParty, whenLabel, directionLabel, type CdrRow } from './cdrShared';

/**
 * כרטיס הקלטה במסך "הקלטות" (עיצוב v2): כפתור השמעה כהה, איש הקשר · נציג,
 * שורת מטא, משך וסנטימנט; "תמלול וסיכום" פותח את התמלול והסיכום בתוך הכרטיס.
 */
export function RecordingCard({ row, index = 0, sourceLabel, playing, busy, failed, onPlay, onOpen }: {
  row: CdrRow;
  /** מיקום בעמוד — לכניסה מדורגת (0.02 + i·0.03 כמו pageOf בעיצוב). */
  index?: number;
  sourceLabel: string | null;
  playing: boolean;
  busy: boolean;
  failed?: boolean;
  onPlay: () => void;
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<CdrDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // התמלול נטען רק כשפותחים אותו, ופעם אחת לכל כרטיס.
  useEffect(() => {
    if (!open || detail) return;
    let active = true;
    setLoading(true);
    void adminApi.cdrDetail(row.id)
      .then((d) => { if (active) setDetail(d); })
      .catch(() => { if (active) toast('שגיאה בטעינת פרטי השיחה', 'danger'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, detail, row.id]);

  const party = remoteParty(row);
  const phone = formatIsraeli(party.number);
  const meta = [sourceLabel ?? directionLabel(row.direction), whenLabel(row.started_at)].join(' · ');

  return (
    <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, ['--d' as string]: `${0.02 + index * 0.03}s` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
          <PlayButton playing={playing} busy={busy} failed={failed} onClick={onPlay} size={46} strong />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 'var(--t-lg)', fontWeight: 600 }}>
              {party.lookup?.name || <span dir="ltr">{phone}</span>}
              {row.agent_name && <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}> · {row.agent_name}</span>}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              {meta}
              {party.lookup?.name && <> · <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{phone}</span></>}
            </span>
          </div>
        </div>
        {row.ai_summary && (
          <p style={{
            flex: '2 1 280px', minWidth: 0, margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={row.ai_summary}>{row.ai_summary}</p>
        )}
        <span style={{ fontSize: 'var(--t-sm)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)', minWidth: 48, textAlign: 'center' }} title="זמן דיבור">
          {fmt(row.talk_sec || row.duration_sec)}
        </span>
        {row.ai_sentiment && <SentimentBadge value={row.ai_sentiment} />}
      </div>

      {open && (
        <div className="cdr-reveal" style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--glass-soft)' }}>
          <RecordingDetail detail={detail} loading={loading} onDetailChange={setDetail} showPlayer={false} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="btn soft cdr-act" style={{ height: 32 }} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'הסתרת תמלול' : 'תמלול וסיכום'}
        </button>
        <button type="button" className="btn soft cdr-act" style={{ height: 32 }} onClick={onOpen}>פרטי השיחה</button>
      </div>
    </div>
  );
}
